import { VALIDATION, WEBINAR_TIMEZONE } from "../constants";

/** Today's calendar date in the webinar timezone (YYYY-MM-DD). */
export function todayYmdInTimezone(
  tz: string = WEBINAR_TIMEZONE,
  now = new Date()
): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value || "00";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Current wall-clock HH:mm in the webinar timezone. */
export function nowHmInTimezone(
  tz: string = WEBINAR_TIMEZONE,
  now = new Date()
): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value || "00";
  // en-GB can return "24" for midnight in some engines — normalize.
  let hour = Number(get("hour"));
  if (hour === 24) hour = 0;
  return `${String(hour).padStart(2, "0")}:${get("minute")}`;
}

/** Round HH:mm up to the next `step` minutes (default 5). */
export function ceilHmToStep(hm: string, step = 5): string {
  const [h, m] = hm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hm;
  const total = h * 60 + m;
  const ceiled = Math.ceil(total / step) * step;
  if (ceiled >= 24 * 60) return "23:55";
  const hh = Math.floor(ceiled / 60);
  const mm = ceiled % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/**
 * Earliest publishable start (now + lead hours) as IST date + time.
 * Time is rounded up to 5-minute steps.
 */
export function earliestAllowedStart(
  now = new Date(),
  leadHours = VALIDATION.startMinHours
): { date: string; time: string } {
  const earliest = new Date(now.getTime() + leadHours * 3600000);
  const date = todayYmdInTimezone(WEBINAR_TIMEZONE, earliest);
  const time = ceilHmToStep(nowHmInTimezone(WEBINAR_TIMEZONE, earliest));
  return { date, time };
}

/** Session date must be today or later (Asia/Kolkata calendar). */
export function isSessionDateNotPast(
  date: string,
  now = new Date()
): boolean {
  if (!date?.trim()) return false;
  return date.trim() >= todayYmdInTimezone(WEBINAR_TIMEZONE, now);
}

/**
 * Start datetime must be at least `leadHours` from now (IST wall clock).
 * Also implies date is not in the past.
 */
export function isSessionStartAllowed(
  date: string,
  time: string,
  now = new Date(),
  leadHours = VALIDATION.startMinHours
): boolean {
  if (!date?.trim() || !time?.trim()) return false;
  if (!isSessionDateNotPast(date, now)) return false;
  const { start } = deriveStartAt(date, time);
  if (!Number.isFinite(start)) return false;
  return start >= now.getTime() + leadHours * 3600000;
}

/**
 * Minimum `HH:mm` for the time input when `date` is today (IST).
 * Future dates have no min. Past dates return end-of-day (should be blocked by date min).
 */
export function minTimeForSessionDate(
  date: string,
  now = new Date(),
  leadHours = VALIDATION.startMinHours
): string | undefined {
  if (!date?.trim()) return undefined;
  const earliest = earliestAllowedStart(now, leadHours);
  if (date > earliest.date) return undefined;
  if (date < earliest.date) return "23:59";
  return earliest.time;
}

/**
 * Treat date (YYYY-MM-DD) + time (HH:mm) as Asia/Kolkata wall clock.
 * IST is UTC+05:30 year-round (no DST).
 */
export function deriveStartAt(
  date: string,
  time: string
): { startAt: string; start: number; timezone: typeof WEBINAR_TIMEZONE } {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = (time || "00:00").split(":").map(Number);

  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) {
    return {
      startAt: "",
      start: Number.NaN,
      timezone: WEBINAR_TIMEZONE,
    };
  }

  // IST → UTC: subtract 5h30m
  const utcMs = Date.UTC(y, m - 1, d, hh - 5, mm - 30, 0, 0);

  return {
    startAt: new Date(utcMs).toISOString(),
    start: utcMs,
    timezone: WEBINAR_TIMEZONE,
  };
}

export function sessionWhenParts(start: number, mins: number) {
  const d = new Date(start);
  const parts = new Intl.DateTimeFormat("en-IN", {
    timeZone: WEBINAR_TIMEZONE,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(d);

  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value || "";

  const weekday = get("weekday");
  const day = get("day");
  const month = get("month");
  const hour = get("hour");
  const minute = get("minute");
  const dayPeriod = (get("dayPeriod") || "").toLowerCase();

  return {
    dateLine: `${weekday}, ${day} ${month}`,
    timeLine: `${hour}:${minute} ${dayPeriod} · ${mins} min`,
    singleLine: `${weekday}, ${day} ${month} · ${hour}:${minute} ${dayPeriod} · ${mins} min`,
    durationLine: `${mins} min`,
  };
}

export function formatSessionDateParts(
  start: number,
  mins: number
): { dateLine: string; timeLine: string } {
  const { dateLine, timeLine } = sessionWhenParts(start, mins);
  return { dateLine, timeLine };
}

export function formatSessionWhen(start: number, mins: number): string {
  return sessionWhenParts(start, mins).singleLine;
}
